/**
 * usePendingCustomerReview — CHG-0186 (Bill 2026-11-04: "Trigger the CIC workflow on detection").
 *
 * Before an application goes to signing (Review → Approve Cart), every existing customer on it is
 * compared with live Core Director (GET /v3/carts/{id}/pending-customer-changes): owners, signers,
 * linked beneficiaries, and an applicant entered as new whose tax ID Core already holds (an online
 * application import). For each one whose application values differ, the SAME Change in Contact
 * dialog as the People step opens (useProfileChange), one customer at a time, with a line saying why:
 *   Confirm → written to Core now (CustMod + account waterfall + notes + insider scan), stamped on the
 *             application so the commit does not repeat it;
 *   Discard → the application takes Core Director's values.
 * After each decision the application is saved and the list is read again, so a customer is never
 * asked twice and nothing is decided on a stale list. `run` resolves ok=true only when nothing is left
 * to review. Core unreachable (for the list or for one customer) keeps signing blocked with Try again.
 *
 * The server enforces the same rule: the pre-render CIF pass refuses an unreviewed difference
 * (CartCommitService.UpdateExistingCifAsync / StopOnUnreviewedAdoptionAsync).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { CartDto, PendingCustomerChangeItem, PendingCustomerChangesResponse } from '@retail-ops/shared-types';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useProfileChange, type ProfileChangeNotice } from '@/components/customer/useProfileChange';
import { readProfileChangeMode } from '@/components/customer/profileChange';
import type { RaoPerson } from '../types';
import { parsePeople } from '../utils/cartStateParse';
import { authorizedByPrefill } from '@/components/customer/authorizedBy';
import {
  applyCoreOutcome,
  benefIsLinkedIndividual,
  benefToProfilePerson,
  type BenefCoreFields,
  type BenefPhoneSlot,
} from '../components/beneficiaryCoreEdit';

export interface PendingReviewResult {
  /** True when signing may start: every difference was confirmed or discarded. */
  ok: boolean;
  /** One line per customer reviewed, plus the reason when it stopped. */
  notices: ProfileChangeNotice[];
}

export interface UsePendingCustomerReviewResult {
  run: (cartId: string, onCartUpdated?: (cart: CartDto) => void) => Promise<PendingReviewResult>;
  /** True while the application is being compared with Core Director. */
  checking: boolean;
  /** Render once in the host's tree. */
  modal: JSX.Element | null;
}

/** The dialog heading when a difference is found before signing. */
export const PENDING_REVIEW_TITLE = 'This customer\'s information on the application differs from Core Director';

const ROLE_LABELS: Record<string, string> = {
  primary: 'Primary owner',
  joint: 'Joint owner',
  signer: 'Authorized signer',
  beneficiary: 'Beneficiary',
  poa: 'Power of attorney',
  custodian: 'Custodian',
  minor: 'Minor',
};

export function roleLabel(role: string): string {
  const r = (role ?? '').trim();
  return ROLE_LABELS[r.toLowerCase()] ?? (r ? r.charAt(0).toUpperCase() + r.slice(1) : 'Applicant');
}

/** The "why" line at the top of the Change in Contact dialog. */
export function pendingReason(item: PendingCustomerChangeItem, remaining: number): { title: string; detail: string } {
  const who = `${item.displayName} (${roleLabel(item.role)}, CIF ${item.cifId})`;
  const match = item.matchedByTaxId
    ? ' This applicant was entered as a new customer, but Core Director already has a customer with the same tax ID.'
    : '';
  const left = remaining > 1 ? ` ${remaining - 1} more customer${remaining - 1 === 1 ? '' : 's'} to review after this one.` : '';
  return {
    title: PENDING_REVIEW_TITLE,
    detail: `${who}.${match} Nobody has reviewed this difference yet. Confirm writes the application's values to Core Director now `
      + '(with the account updates and notes). Discard puts Core Director\'s values back on the application. '
      + `Signing starts only after every difference is confirmed or discarded.${left}`,
  };
}

function readRoster(cart: CartDto, instanceId: string): Array<Record<string, unknown>> {
  if (!cart.metadataJson) return [];
  try {
    const meta = JSON.parse(cart.metadataJson) as Record<string, unknown>;
    const map = meta['beneficiaries'];
    if (map && typeof map === 'object' && !Array.isArray(map)) {
      const rows = (map as Record<string, unknown>)[instanceId];
      if (Array.isArray(rows)) return rows.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object');
    }
  } catch { /* unreadable → empty */ }
  return [];
}

function sameCif(a: unknown, cif: string): boolean {
  return typeof a === 'string' && a.trim() === cif.trim();
}

interface Blocked {
  title: string;
  message: string;
}

const MAX_ROUNDS = 25;

export function usePendingCustomerReview(): UsePendingCustomerReviewResult {
  const client = useApiClient();
  const { config } = useConfig();
  const { begin, modal: profileModal } = useProfileChange();
  const [checking, setChecking] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  // While the blocked dialog is open, run's promise waits on the officer: Try again re-runs, Close stops.
  const blockedResolver = useRef<((retry: boolean) => void) | null>(null);

  const waitOnBlocked = useCallback((b: Blocked): Promise<boolean> => {
    setBlocked(b);
    return new Promise<boolean>((resolve) => { blockedResolver.current = resolve; });
  }, []);

  const closeBlocked = useCallback((retry: boolean): void => {
    const r = blockedResolver.current;
    blockedResolver.current = null;
    setBlocked(null);
    r?.(retry);
  }, []);

  const run = useCallback(
    async (cartId: string, onCartUpdated?: (cart: CartDto) => void): Promise<PendingReviewResult> => {
      const notices: ProfileChangeNotice[] = [];
      const mode = readProfileChangeMode(config.systemSettings);
      const stop = (text: string): PendingReviewResult => ({ ok: false, notices: [...notices, { tone: 'err', text }] });
      for (let round = 0; round < MAX_ROUNDS; round++) {
        // 1. Which existing customers differ from Core now?
        let resp: PendingCustomerChangesResponse | null = null;
        let failure: string | null = null;
        setChecking(true);
        try {
          resp = await client.retail.carts.pendingCustomerChanges(cartId);
          if (!resp.success) failure = resp.error ?? 'The application could not be compared with Core Director.';
        } catch (err) {
          failure = err instanceof Error ? err.message : String(err);
        } finally {
          setChecking(false);
        }
        if (!failure && resp && resp.unreachable.length > 0) {
          const names = resp.unreachable.map((u) => `${u.displayName}${u.cifId ? ` (CIF ${u.cifId})` : ''}`).join(', ');
          failure = `Core Director could not be reached to compare ${names}. ${resp.unreachable[0]?.message ?? ''}`.trim();
        }
        if (failure || !resp) {
          const retry = await waitOnBlocked({ title: 'Core Director could not be reached', message: failure ?? '' });
          if (retry) continue;
          return stop('Signing was not started. Every existing customer on the application must be compared with Core Director first.');
        }
        if (resp.items.length === 0) return { ok: true, notices };

        // 2. The first one: the same Change in Contact dialog, with the reason.
        const item = resp.items[0]!;
        const reason = pendingReason(item, resp.items.length);
        let cart: CartDto | undefined;
        try {
          const got = await client.retail.carts.get(cartId);
          cart = got.success ? got.cart : undefined;
        } catch { cart = undefined; }
        if (!cart) return stop('The application could not be reloaded. Try Approve Cart again.');
        // CHG-0186: "Authorized by" starts with the first customer on an officer-created in-branch application.
        const prefill = authorizedByPrefill(cart);

        if (item.kind === 'person') {
          const person = parsePeople(cart).find((p) => (item.partyKey ? p.id === item.partyKey : sameCif(p.cifId, item.cifId)));
          const partyKey = person?.id ?? item.partyKey ?? undefined;
          if (!person || !partyKey) return stop(`${item.displayName} could not be found on the application. Reload and try again.`);
          // An applicant entered as new whose tax ID Core holds: reviewed against that record and linked to it.
          const subject: RaoPerson = item.matchedByTaxId ? { ...person, cifId: item.cifId } : person;
          const outcome = await begin(subject, { source: 'rao-pending-review', cartId, surface: 'cart', reason, authorizedByPrefill: prefill });
          if (!outcome.proceed) return stop(outcome.notice?.text ?? 'Signing was not started.');
          const toSave: RaoPerson = { ...outcome.person, id: partyKey, cifId: item.cifId };
          try {
            const res = await client.retail.carts.updateParty(cartId, partyKey, { person: toSave as unknown as Record<string, unknown> });
            if (!res.success) return stop(res.error ?? `${item.displayName}'s review could not be saved on the application.`);
            onCartUpdated?.(res.cart);
          } catch (err) {
            return stop(err instanceof Error ? err.message : String(err));
          }
          if (outcome.notice) notices.push({ ...outcome.notice, text: `${item.displayName} (CIF ${item.cifId}): ${outcome.notice.text}` });
          continue;
        }

        // Linked beneficiary: one review, applied to the row on every product line it is on.
        const slot = (item.phoneSlot ?? 'phoneCell') as BenefPhoneSlot;
        const isTarget = (e: Record<string, unknown>): boolean =>
          sameCif(e['cifId'], item.cifId) && benefIsLinkedIndividual(e as unknown as BenefCoreFields);
        const first = item.instanceIds.flatMap((id) => readRoster(cart!, id)).find(isTarget);
        if (!first) return stop(`Beneficiary ${item.displayName} could not be found on the application. Reload and try again.`);
        const outcome = await begin(benefToProfilePerson(first as unknown as BenefCoreFields & { id?: string }, slot), {
          source: 'rao-pending-review', cartId, surface: 'cart', reason, authorizedByPrefill: prefill,
        });
        if (!outcome.proceed) return stop(outcome.notice?.text ?? 'Signing was not started.');
        for (const instanceId of item.instanceIds) {
          const rows = readRoster(cart, instanceId);
          const next = rows.map((e) => (isTarget(e)
            ? (applyCoreOutcome(e as unknown as BenefCoreFields, outcome, slot, mode) as unknown as Record<string, unknown>)
            : e));
          try {
            const res = await client.retail.carts.setLineItemBeneficiaries(cartId, instanceId, next);
            if (!res.success) return stop(res.error ?? `Beneficiary ${item.displayName}'s review could not be saved on the application.`);
            onCartUpdated?.(res.cart);
          } catch (err) {
            return stop(err instanceof Error ? err.message : String(err));
          }
        }
        if (outcome.notice) notices.push({ ...outcome.notice, text: `Beneficiary ${item.displayName} (CIF ${item.cifId}): ${outcome.notice.text}` });
      }
      return stop('Too many changes to review in one pass. Choose Approve Cart again.');
    },
    [client, config.systemSettings, begin, waitOnBlocked],
  );

  const modal = useMemo((): JSX.Element | null => {
    if (blocked) {
      return (
        <ModalShell
          open
          onClose={() => closeBlocked(false)}
          title={blocked.title}
          size="md"
          disableBackdropClose
          footer={
            <>
              <Button variant="ghost" onClick={() => closeBlocked(false)}>Close</Button>
              <Button variant="primary" onClick={() => closeBlocked(true)}>Try again</Button>
            </>
          }
        >
          <Banner tone="err" title="Signing cannot start yet">
            {blocked.message} Every existing customer on the application is compared with Core Director before
            signing, so a difference nobody reviewed never reaches the documents. Nothing was written.
          </Banner>
        </ModalShell>
      );
    }
    return profileModal;
  }, [blocked, closeBlocked, profileModal]);

  return { run, checking, modal };
}
